import { Brand, Category, VariantAttribute } from "./../../types/AppTypes";
import { EnrichedProduct, ProductInfo, ProductVariant } from "@/types/AppTypes";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import combineSingleProduct from "@/utils/helper/CombineSingleProduct";
import capitalizeWords from "@/utils/helper/capitalizeWords";

interface SellerState {
  loading: boolean;
  productInfos: ProductInfo[];
  productsForDisplay: any[];
  enrichedProductList: EnrichedProduct[];
  brandsLoading: boolean;
  categoriesLoading: boolean;
  productsLoading: boolean;
  allVariantAttributes: VariantAttribute[];
  shouldFetchProducts: boolean;
  brands?: Brand[];
  categories?: Category[];
}

const initialState: SellerState = {
  loading: false,
  productInfos: [],
  productsForDisplay: [],
  enrichedProductList: [],
  productsLoading: false,
  brandsLoading: false,
  categoriesLoading: false,
  allVariantAttributes: [],
  shouldFetchProducts: false,
  brands: [],
  categories: [],
};

const sellerSlice = createSlice({
  name: "seller",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setProductInfos: (state, action: PayloadAction<ProductInfo[]>) => {
      state.productInfos = action.payload;
    },
    setProductsForDisplay: (state, action: PayloadAction<any>) => {
      state.productsForDisplay = action.payload;
    },
    setProductsLoading: (state, action: PayloadAction<boolean>) => {
      state.productsLoading = action.payload;
    },
    setEnrichedProductList: (state, action: PayloadAction<EnrichedProduct[]>) => {
      state.enrichedProductList = action.payload;
    },
    setBrands: (state, action: PayloadAction<Brand[]>) => {
      state.brands = action.payload;
    },
    setCategories: (state, action: PayloadAction<Category[]>) => {
      state.categories = action.payload;
    },

    addProduct: (state, action: PayloadAction<any>) => {
      const newProduct = action.payload;


      const indexEn = state.enrichedProductList.findIndex(
        (EnrichedProduct) => EnrichedProduct.id === newProduct?.id
      );
      if (indexEn !== -1) {
        state.enrichedProductList[indexEn] = newProduct;
      } else {
        state.enrichedProductList.push(newProduct);
      }

 
      const variants = Array.isArray(newProduct.variants)
        ? newProduct.variants
            .map((v: any) => {
              if (typeof v === "string") {
                try {
                  return JSON.parse(v);
                } catch (e) {
                  console.error("Failed to parse variant JSON:", v);
                  return null;
                }
              }
              return v;
            })
            .filter(Boolean)
        : [];

      const selectedVariant =
        variants.find((v: ProductVariant) => v?.show_by_default) || variants[0] || null;

      if (selectedVariant) {
        selectedVariant.product_id = newProduct.id;
        selectedVariant.name = capitalizeWords(newProduct.name || "");
        selectedVariant.seller_id = newProduct.seller_id;
      }

      const combined = combineSingleProduct(
        selectedVariant,
        state.enrichedProductList,
        state.brands || [],
        state.categories || []
      );

      const index = state.productsForDisplay.findIndex(
        (product) => product?.product_id === newProduct?.id
      );
      if (combined) {
        if (index !== -1) {
          state.productsForDisplay[index] = combined;
        } else {
          state.productsForDisplay.push(combined);
        }
      }
    },

    setAllVariantAttributes: (state, action: PayloadAction<VariantAttribute[]>) => {
      state.allVariantAttributes = action.payload;
    },
    setShouldFetchProducts: (state, action: PayloadAction<boolean>) => {
      state.shouldFetchProducts = action.payload;
    },
    removeProductInfo: (state, action: PayloadAction<string>) => {
      const productId = action.payload;
      state.productInfos = state.productInfos.filter(
        (product) => product.id !== productId
      );
      state.productsForDisplay = state.productsForDisplay.filter(
        (product) => product.product_id !== productId
      );
      state.enrichedProductList = state.enrichedProductList.filter(
        (product) => product.id !== productId
      );
    }
  },
});

export const {
  setLoading,
  setProductInfos,
  setProductsForDisplay,
  setProductsLoading,
  setEnrichedProductList,
  addProduct,
  setAllVariantAttributes,
  setShouldFetchProducts,
  setBrands,
  setCategories,
  removeProductInfo,
} = sellerSlice.actions;

export default sellerSlice.reducer;
